/**
 * Navbar Component
 *
 * Top navigation bar for the DiscordMinstrel platform.
 * Displays brand, user info, and navigation actions.
 */

import { useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, type User } from '../../contexts/AuthContext';

// =============================================================================
// Types
// =============================================================================

interface NavbarProps {
  /** Whether the sidebar is currently open */
  isSidebarOpen: boolean;
  /** Callback to toggle sidebar visibility */
  onToggleSidebar: () => void;
}

interface UserMenuProps {
  user: User;
  onLogout: () => void;
}

// =============================================================================
// Sub-components
// =============================================================================

/**
 * Hamburger menu icon for mobile sidebar toggle
 */
function MenuIcon({ isOpen }: { isOpen: boolean }) {
  return (
    <svg
      className="h-6 w-6"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      {isOpen ? (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M6 18L18 6M6 6l12 12"
        />
      ) : (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M4 6h16M4 12h16M4 18h16"
        />
      )}
    </svg>
  );
}

/**
 * User dropdown menu
 */
function UserMenu({ user, onLogout }: UserMenuProps) {
  const [isOpen, setIsOpen] = useState(false);

  const handleToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const handleLogout = useCallback(() => {
    setIsOpen(false);
    onLogout();
  }, [onLogout]);

  const handleClickOutside = useCallback(() => {
    setIsOpen(false);
  }, []);

  /**
   * Get role display name
   */
  const getRoleDisplay = (role: User['role']): string => {
    switch (role) {
      case 'GLOBAL_ADMIN':
        return 'Global Admin';
      case 'TENANT_ADMIN':
        return 'Tenant Admin';
      case 'TENANT_USER':
        return 'User';
      case 'VIEWER':
        return 'Viewer';
      default:
        return role;
    }
  };

  return (
    <div className="relative">
      <button
        onClick={handleToggle}
        className="flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors hover:bg-discord-light"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {/* User Avatar */}
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-discord-blurple text-white">
          {user.name.charAt(0).toUpperCase()}
        </div>
        <div className="hidden text-left md:block">
          <div className="font-medium text-white">{user.name}</div>
          <div className="text-xs text-gray-400">{getRoleDisplay(user.role)}</div>
        </div>
        {/* Chevron */}
        <svg
          className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <>
          {/* Backdrop for clicking outside */}
          <div
            className="fixed inset-0 z-10"
            onClick={handleClickOutside}
            aria-hidden="true"
          />
          <div className="absolute right-0 z-20 mt-2 w-48 rounded-md bg-discord-darker py-1 shadow-lg ring-1 ring-black ring-opacity-5">
            <div className="border-b border-discord-light px-4 py-2 md:hidden">
              <div className="font-medium text-white">{user.name}</div>
              <div className="text-xs text-gray-400">{getRoleDisplay(user.role)}</div>
            </div>
            <Link
              to="/profile"
              className="block px-4 py-2 text-sm text-gray-300 hover:bg-discord-light hover:text-white"
              onClick={() => setIsOpen(false)}
            >
              Profile Settings
            </Link>
            <button
              onClick={handleLogout}
              className="block w-full px-4 py-2 text-left text-sm text-gray-300 hover:bg-discord-light hover:text-white"
            >
              Sign Out
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// =============================================================================
// Component
// =============================================================================

/**
 * Main navigation bar component
 */
export function Navbar({ isSidebarOpen, onToggleSidebar }: NavbarProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = useCallback(async () => {
    await logout();
    navigate('/login', { replace: true });
  }, [logout, navigate]);

  return (
    <header className="fixed left-0 right-0 top-0 z-30 border-b border-discord-light bg-discord-darkest">
      <div className="flex h-16 items-center justify-between px-4">
        {/* Left Section */}
        <div className="flex items-center gap-4">
          {/* Mobile menu toggle */}
          <button
            onClick={onToggleSidebar}
            className="rounded-md p-2 text-gray-400 transition-colors hover:bg-discord-light hover:text-white lg:hidden"
            aria-label={isSidebarOpen ? 'Close sidebar' : 'Open sidebar'}
          >
            <MenuIcon isOpen={isSidebarOpen} />
          </button>

          {/* Brand */}
          <Link
            to="/dashboard"
            className="flex items-center gap-2 text-xl font-bold"
          >
            <span className="text-discord-blurple">Discord</span>
            <span className="text-white">Minstrel</span>
          </Link>
        </div>

        {/* Right Section */}
        <div className="flex items-center gap-4">
          {/* Notifications (placeholder) */}
          <button
            className="rounded-md p-2 text-gray-400 transition-colors hover:bg-discord-light hover:text-white"
            aria-label="Notifications"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
              />
            </svg>
          </button>

          {/* User Menu */}
          {user && <UserMenu user={user} onLogout={() => void handleLogout()} />}
        </div>
      </div>
    </header>
  );
}

export default Navbar;
